import { useMemo } from "react";

import { useLobApi } from "../../hooks/useLobApi";
import type { YoYChange } from "../../types";
import { calculateChangeFromLastMonth } from "../helpers";

type LobMonthlyEntry = {
    Lob: string;
    lobKey: string;
    LobVisits: number;
    Share: number;
    YoYChange: YoYChange;
    MonthlyVisits: { [date: string]: { Visits: number } };
};

type LOBDataTableResponse = {
    Data: Record<string, LobMonthlyEntry[]>;
};

type Trend = {
    key: string;
    value: number;
};

interface TransformedDataTable {
    lobName: string;
    totalVisits: { visits: number; share?: number };
    visitsTrend?: Trend[];
    trafficShare?: Record<string, number>;
    yoyChange?: number;
    domainYoyChanges?: Record<string, { currentPeriodVisits: number; comparePeriodVisits: number }>;
}

const ENDPOINT = "api/websiteContent/lob/Table";

const getYoYChangeData = (YoYChange: LobMonthlyEntry["YoYChange"]) => ({
    currentPeriodVisits: YoYChange?.CurrentPeriodVisits || 0,
    comparePeriodVisits: YoYChange?.ComparePeriodVisits || 0,
});

const transformMonthlyVisitsToTrend = (MonthlyVisits: LobMonthlyEntry["MonthlyVisits"]): Trend[] => {
    if (!MonthlyVisits) {
        return [];
    }

    return Object.entries(MonthlyVisits).map(([date, { Visits }]) => ({
        key: date,
        value: Visits,
    }));
};

const processCompareModeData = (responseData: Record<string, LobMonthlyEntry[]>): TransformedDataTable[] => {
    const lobGroups = new Map<string, { site: string; visits: number; yoyChange: any }[]>();

    Object.entries(responseData).forEach(([site, lobs]) => {
        lobs.forEach(({ Lob, LobVisits, YoYChange }) => {
            if (!lobGroups.has(Lob)) {
                lobGroups.set(Lob, []);
            }
            lobGroups.get(Lob).push({ site, visits: LobVisits, yoyChange: YoYChange });
        });
    });

    return Array.from(lobGroups.entries())
        .map(([lobName, entries]) => {
            const totalVisits = entries.reduce((sum, { visits }) => sum + visits, 0);
            const aggregatedYoY = entries.reduce(
                (acc, { yoyChange }) => {
                    const { currentPeriodVisits, comparePeriodVisits } = getYoYChangeData(yoyChange);
                    return {
                        currentPeriodVisits: acc.currentPeriodVisits + currentPeriodVisits,
                        comparePeriodVisits: acc.comparePeriodVisits + comparePeriodVisits,
                    };
                },
                { currentPeriodVisits: 0, comparePeriodVisits: 0 },
            );

            return {
                lobName,
                totalVisits: { visits: totalVisits },
                trafficShare: Object.fromEntries(
                    entries.map(({ site, visits }) => [site, totalVisits > 0 ? visits / totalVisits : 0]),
                ),
                yoyChange: calculateChangeFromLastMonth(aggregatedYoY),
                domainYoyChanges: Object.fromEntries(
                    entries.map(({ site, yoyChange }) => [site, getYoYChangeData(yoyChange)]),
                ),
            };
        })
        .filter(({ totalVisits }) => totalVisits.visits > 0);
};

const processSingleModeData = (responseData: Record<string, LobMonthlyEntry[]>): TransformedDataTable[] => {
    const siteLobs = Object.values(responseData)[0] || [];

    return siteLobs
        .filter(({ LobVisits }) => LobVisits > 0)
        .map(({ Lob, LobVisits, Share, MonthlyVisits, YoYChange }) => {
            const { currentPeriodVisits, comparePeriodVisits } = getYoYChangeData(YoYChange);

            return {
                lobName: Lob,
                totalVisits: { visits: LobVisits, share: Share },
                yoyChange: calculateChangeFromLastMonth({
                    currentPeriodVisits: currentPeriodVisits,
                    comparePeriodVisits: comparePeriodVisits,
                }),
                visitsTrend: transformMonthlyVisitsToTrend(MonthlyVisits),
            };
        });
};

const useLobDataTable = (isCompareMode: boolean) => {
    const { data: response, isLoading, isError } = useLobApi<LOBDataTableResponse>(ENDPOINT, "lob-table-chart");

    const data = useMemo<TransformedDataTable[]>(() => {
        if (!response?.Data) {
            return [];
        }

        return isCompareMode ? processCompareModeData(response.Data) : processSingleModeData(response.Data);
    }, [response, isCompareMode]);

    const filteredLobsData = useMemo(() => {
        if (!response?.Data || isCompareMode) {
            return [];
        }
        const siteLobs = Object.values(response.Data)[0] || [];
        return siteLobs.map(({ Lob, lobKey }) => ({ Lob, lobKey }));
    }, [response, isCompareMode]);

    return { data, isLoading, isError, filteredLobsData };
};

export default useLobDataTable;
